/**
 * Static store locator data for the cash-deposit gallery.
 *
 * The JSON under `fixtures/` is a copy of the InComm VRS dumps (native VRS
 * vocabulary, sampled by ZIP). The gallery never calls an API, so this module
 * is the whole locator: it maps the dump to the canonical store shape the
 * widget renders and answers "which stores are near this point?" in memory.
 *
 * `distanceInMiles` in the dump is measured from the ZIP used to capture it —
 * it is never reused; distance is recomputed with haversine from the point the
 * screen is centred on.
 */

import merchantsDump from './fixtures/vrs-merchants.json';
import locationsDump from './fixtures/vrs-locations-merged.json';

const NEARBY_RADIUS_MILES = 40;
const NEARBY_LIMIT = 30;
const EARTH_RADIUS_MILES = 3958.7613;

/** Brand logos, downloaded from the Lola Cash merchants CDN so the gallery
 *  renders offline. Keyed by VRS `merchantID` (`6489.svg`). */
const MERCHANT_LOGOS = import.meta.glob('../assets/merchants/*.svg', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const LOGO_BY_MERCHANT_ID = new Map<string, string>(
  Object.entries(MERCHANT_LOGOS).map(([path, url]) => [
    path.replace(/^.*\/(.+)\.svg$/, '$1'),
    url,
  ]),
);

export interface CashDepositStoreAddress {
  line1: string;
  city: string;
  state: string;
  zip: string;
  countryISO2: string;
  latitude: number;
  longitude: number;
}

export interface CashDepositStore {
  storeId: string;
  merchantId: string;
  name: string;
  /** Local asset URL, or `null` when the chain has no bundled logo. */
  logoUrl: string | null;
  address: CashDepositStoreAddress;
  /** Haversine miles from the query point. Omitted when no coords were given. */
  distanceMiles?: number;
}

export interface CashDepositChain {
  merchantId: string;
  name: string;
  logoUrl: string | null;
  /** US states where the dump has at least one store for this chain. */
  states: string[];
}

export interface GeoPoint {
  latitude: number;
  longitude: number;
}

/**
 * Stand-in for the IP geolocation the backend hands the widget when the device
 * denies the browser prompt. Miami: the dump has ~39 stores in that metro.
 */
export const CASH_DEPOSIT_MOCK_IP_LOCATION = {
  latitude: 25.7743,
  longitude: -80.1937,
  city: 'Miami',
  state: 'FL',
} as const;

interface VrsMerchantRow {
  merchantID: string;
  merchantName: string;
}

interface VrsLocationRow {
  merchantID: string;
  merchantName?: string | null;
  address?: {
    addressLine1?: string | null;
    city?: string | null;
    postalCode?: string | null;
    state?: string | null;
    country?: string | null;
    latitude?: string | null;
    longitude?: string | null;
  };
}

function parseCoord(value: string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function isFiniteLatLng(lat: number, lng: number): boolean {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

const toRad = (deg: number) => (deg * Math.PI) / 180;

export function haversineMiles(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.min(1, Math.sqrt(a)));
}

function toCountryIso2(country: string | null | undefined): string {
  const n = (country ?? '').trim().toUpperCase();
  if (n === 'USA' || n === 'UNITED STATES' || n === 'US') return 'US';
  return n.length === 2 ? n : 'US';
}

const CATALOG_ROWS: VrsMerchantRow[] = (
  (merchantsDump as { Merchants?: VrsMerchantRow[] }).Merchants ?? []
).filter((row) => row.merchantID && row.merchantName);

const CATALOG_BY_ID = new Map(CATALOG_ROWS.map((row) => [row.merchantID, row]));

interface ParsedLocation extends CashDepositStoreAddress {
  merchantId: string;
  name: string;
}

/**
 * The locations dump is not scoped to the barcode catalog (it carries Target,
 * Duane Reade `3596`, …). Rows outside `vrs-merchants.json` are dropped so the
 * map never offers a chain that cannot mint a barcode.
 */
function parseLocation(row: VrsLocationRow): ParsedLocation | null {
  const catalog = CATALOG_BY_ID.get(row.merchantID);
  if (!catalog) return null;
  const addr = row.address;
  if (!addr) return null;
  const latitude = parseCoord(addr.latitude);
  const longitude = parseCoord(addr.longitude);
  if (latitude == null || longitude == null) return null;
  if (!isFiniteLatLng(latitude, longitude)) return null;
  return {
    merchantId: row.merchantID,
    name: (row.merchantName ?? '').trim() || catalog.merchantName,
    line1: (addr.addressLine1 ?? '').trim(),
    city: (addr.city ?? '').trim(),
    state: (addr.state ?? '').trim().toUpperCase(),
    zip: (addr.postalCode ?? '').trim(),
    countryISO2: toCountryIso2(addr.country),
    latitude,
    longitude,
  };
}

const PARSED_LOCATIONS: ParsedLocation[] = (
  (locationsDump as { merchants?: VrsLocationRow[] }).merchants ?? []
)
  .map(parseLocation)
  .filter((row): row is ParsedLocation => row != null);

const STATES_BY_MERCHANT = new Map<string, string[]>();
for (const loc of PARSED_LOCATIONS) {
  if (!loc.state) continue;
  const current = STATES_BY_MERCHANT.get(loc.merchantId) ?? [];
  if (!current.includes(loc.state)) {
    current.push(loc.state);
    STATES_BY_MERCHANT.set(loc.merchantId, current);
  }
}

/** VRS never sends a store id, so build a stable synthetic one. */
function buildStoreId(loc: ParsedLocation, index: number): string {
  return `${loc.merchantId}:${loc.latitude}:${loc.longitude}:${index}`;
}

const ALL_STORES: CashDepositStore[] = PARSED_LOCATIONS.map((loc, index) => ({
  storeId: buildStoreId(loc, index),
  merchantId: loc.merchantId,
  name: loc.name,
  logoUrl: LOGO_BY_MERCHANT_ID.get(loc.merchantId) ?? null,
  address: {
    line1: loc.line1,
    city: loc.city,
    state: loc.state,
    zip: loc.zip,
    countryISO2: loc.countryISO2,
    latitude: loc.latitude,
    longitude: loc.longitude,
  },
}));

/** Barcode chains present in the dump, in catalog order. */
export const cashDepositMockChains: CashDepositChain[] = CATALOG_ROWS.map(
  (row) => ({
    merchantId: row.merchantID,
    name: row.merchantName,
    logoUrl: LOGO_BY_MERCHANT_ID.get(row.merchantID) ?? null,
    states: STATES_BY_MERCHANT.get(row.merchantID) ?? [],
  }),
).filter((chain) => chain.states.length > 0);

export interface FindStoresQuery {
  origin?: GeoPoint | null;
  merchantId?: string | null;
  radiusMiles?: number;
  limit?: number;
}

/**
 * With an origin: haversine, 40 mi radius, closest 30, `distanceMiles` set.
 * Without one: the catalog-filtered dump, unranked and with no distance.
 */
export function findMockStores({
  origin,
  merchantId,
  radiusMiles = NEARBY_RADIUS_MILES,
  limit = NEARBY_LIMIT,
}: FindStoresQuery = {}): CashDepositStore[] {
  const candidates = merchantId
    ? ALL_STORES.filter((store) => store.merchantId === merchantId)
    : ALL_STORES;

  if (!origin || !isFiniteLatLng(origin.latitude, origin.longitude)) {
    return candidates.slice(0, limit);
  }

  return candidates
    .map((store) => ({
      ...store,
      distanceMiles:
        Math.round(
          haversineMiles(
            origin.latitude,
            origin.longitude,
            store.address.latitude,
            store.address.longitude,
          ) * 1000,
        ) / 1000,
    }))
    .filter((store) => store.distanceMiles <= radiusMiles)
    .sort((a, b) => a.distanceMiles - b.distanceMiles)
    .slice(0, limit);
}

/** Closest store anywhere in the dump — the escape hatch when the user is
 *  outside every covered metro and the radius search comes back empty. */
export function findNearestMockStore(origin: GeoPoint): CashDepositStore | null {
  if (!isFiniteLatLng(origin.latitude, origin.longitude)) return null;
  let nearest: CashDepositStore | null = null;
  let nearestMiles = Infinity;
  for (const store of ALL_STORES) {
    const miles = haversineMiles(
      origin.latitude,
      origin.longitude,
      store.address.latitude,
      store.address.longitude,
    );
    if (miles < nearestMiles) {
      nearestMiles = miles;
      nearest = { ...store, distanceMiles: Math.round(miles * 1000) / 1000 };
    }
  }
  return nearest;
}

/** Total rows kept from the dump. Shown as the locator's coverage hint. */
export const CASH_DEPOSIT_MOCK_STORE_COUNT = ALL_STORES.length;
